'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { CategoryPillNav } from '../../blog/CategoryPillNav';
import { PostCard } from '../../blog/PostCard';
import { Button } from '../../ui/button';
import { PageType, PAGE_PATHS } from '../../Router';
import {
  getPublishedPostsByCategory,
  type BlogPostDocument, type BlogCategoryDocument,
} from '../../../firebase/blog-db';
import { trackEvent } from '../../../lib/analytics';
import { ArrowLeft, FileQuestion } from 'lucide-react';

const PAGE_SIZE = 12;

interface BlogCategoryPageProps {
  categorySlug: string;
  onNavigate: (page: PageType) => void;
  category: BlogCategoryDocument | null;
  categories: BlogCategoryDocument[];
  posts: BlogPostDocument[];
  hasMore: boolean;
}

export function BlogCategoryPage({ categorySlug, onNavigate, category, categories, posts, hasMore: initialHasMore }: BlogCategoryPageProps) {
  const [morePosts, setMorePosts] = useState<BlogPostDocument[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(posts.at(-1)?.publishedAt ?? undefined);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (category) trackEvent('category_view', { category: category.slug });
  }, [category]);

  const loadMore = async () => {
    if (!category?.id) return;
    setLoadingMore(true);
    try {
      const page = await getPublishedPostsByCategory(category.id, PAGE_SIZE, cursor);
      setMorePosts(prev => [...prev, ...page.posts]);
      setCursor(page.nextCursor);
      setHasMore(page.posts.length === PAGE_SIZE);
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const allPosts = [...posts, ...morePosts];

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <div className="pt-14">
        {category === null ? (
          <div className="max-w-2xl mx-auto px-4 py-24 text-center">
            <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Category not found</h1>
            <p className="text-gray-500 mb-6">This category doesn't exist or may have been removed.</p>
            <Link href={PAGE_PATHS['blog-home']}>
              <Button variant="outline"><ArrowLeft className="w-4 h-4" /> Back to Blog</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="border-b border-gray-100 bg-hustlance-background">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
                <Link href={PAGE_PATHS['blog-home']} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-hustlance-primary transition-colors mb-4">
                  <ArrowLeft className="w-3.5 h-3.5" /> All articles
                </Link>
                <h1 className="font-editorial text-3xl sm:text-4xl text-gray-900">{category?.name}</h1>
                {category?.description && <p className="text-gray-500 mt-2 max-w-xl mx-auto">{category.description}</p>}
              </div>
            </div>
            <CategoryPillNav categories={categories} activeSlug={categorySlug} />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              {error ? (
                <div className="text-center py-20">
                  <p className="text-gray-500 mb-4">We couldn't load these articles right now.</p>
                  <Button onClick={() => window.location.reload()}>Try again</Button>
                </div>
              ) : allPosts.length === 0 ? (
                <div className="text-center py-20">
                  <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No articles in this category yet.</p>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                    {allPosts.map(post => <PostCard key={post.id} post={post} />)}
                  </div>
                  {hasMore && (
                    <div className="text-center mt-14">
                      <Button variant="outline" size="lg" onClick={loadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading...' : 'Load more articles'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </main>
          </>
        )}
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
